import { useEffect, useState } from 'react';
import type { LabResult, Medication, ClinicalDocument } from '@ampshift/shared';
import type { ApiClient } from '../api';
import { useI18n, formatDate } from '../i18n';

interface Props { api: ApiClient }
type Tab = 'results' | 'medications' | 'documents';

/** "My results & records": read-only tool calls; documents open via short-lived presigned URLs in a new tab. */
export function RecordsCard({ api }: Props) {
  const { t, lang } = useI18n();
  const [tab, setTab] = useState<Tab>('results');
  const [labs, setLabs] = useState<LabResult[] | null>(null);
  const [meds, setMeds] = useState<Medication[] | null>(null);
  const [docs, setDocs] = useState<ClinicalDocument[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const fail = (e: unknown) => { if (!cancelled) setError(e instanceof Error ? e.message : t('error')); };
    if (tab === 'results' && labs === null) api.tool('getLabResults', { latestOnly: true }).then((o) => { if (!cancelled) setLabs(o.results); }).catch(fail);
    if (tab === 'medications' && meds === null) api.tool('getMedicationList', { includeInactive: false }).then((o) => { if (!cancelled) setMeds(o.medications); }).catch(fail);
    if (tab === 'documents' && docs === null) api.tool('listDocuments', {}).then((o) => { if (!cancelled) setDocs(o.documents.filter((d) => d.status === 'AVAILABLE')); }).catch(fail);
    return () => { cancelled = true; };
  }, [api, tab, labs, meds, docs, t]);

  const openDocument = async (d: ClinicalDocument) => {
    setError(null);
    try {
      const { url } = await api.tool('getDocumentUrl', { documentId: d.documentId });
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setError(e instanceof Error ? e.message : t('error'));
    }
  };

  const range = (r: LabResult): string => r.referenceRange?.text ?? [r.referenceRange?.low, r.referenceRange?.high].filter((v) => v !== undefined).join('–');

  return (
    <section className="card" aria-labelledby="records-title">
      <h2 id="records-title">{t('recordsTitle')}</h2>
      <div className="tabs" role="tablist">
        {(['results', 'medications', 'documents'] as const).map((k) => (
          <button key={k} role="tab" aria-selected={tab === k} className="tab" onClick={() => { setTab(k); setError(null); }}>{t(k)}</button>
        ))}
      </div>
      {error && <div className="err" role="alert">{error}</div>}
      {tab === 'results' && (labs === null ? <div className="muted">{t('loading')}</div> : labs.length === 0 ? <div className="muted">{t('noResults')}</div> : (
        <div className="tbl-wrap">
          <table className="tbl">
            <thead><tr><th>{t('test')}</th><th>{t('value')}</th><th>{t('reference')}</th><th>{t('flag')}</th><th>{t('collected')}</th></tr></thead>
            <tbody>
              {[...labs].sort((a, b) => a.display.localeCompare(b.display)).map((r) => (
                <tr key={r.resultId}>
                  <td>{r.display}</td>
                  <td>{r.value ?? r.valueText ?? '—'} {r.unit ?? ''}</td>
                  <td className="muted">{range(r)}</td>
                  <td><span className={`flag flag-${r.flag}`}>{r.flag === 'N' ? '' : r.flag}</span></td>
                  <td className="muted">{formatDate(r.collectedAt, lang)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
      {tab === 'medications' && (meds === null ? <div className="muted">{t('loading')}</div> : meds.length === 0 ? <div className="muted">{t('noMedications')}</div> : (
        <ul className="stack" style={{ margin: 0, paddingInlineStart: 18 }}>
          {meds.map((m) => (
            <li key={m.medicationId}>
              <strong>{m.name}</strong> {m.dosage.dose ?? ''} {m.dosage.route ?? ''} {m.dosage.frequency ?? ''}
              <div className="prov">{m.provenance === 'patient-reported' ? t('provenancePatient') : t('verified')}{m.startDate ? ` · ${formatDate(m.startDate, lang)}` : ''}</div>
            </li>
          ))}
        </ul>
      ))}
      {tab === 'documents' && (docs === null ? <div className="muted">{t('loading')}</div> : docs.length === 0 ? <div className="muted">{t('noDocuments')}</div> : (
        <ul className="stack" style={{ margin: 0, paddingInlineStart: 18 }}>
          {docs.map((d) => (
            <li key={d.documentId} className="row">
              <span>{d.title} <span className="muted">· {d.category} · {formatDate(d.recordedAt, lang)}</span></span>
              <button className="btn btn-sm" onClick={() => void openDocument(d)}>{t('open')}</button>
            </li>
          ))}
        </ul>
      ))}
    </section>
  );
}
